// apps/web/src/pages/admin/reservations/ExportButton.tsx —
// Phase 6 plan 06-04 (EXP-01..03).
//
// Presentational component for the B4 Reservations export row. Two
// buttons (CSV + PDF) trigger the `useExportReservations` mutation
// hook; on success, the hook opens the signed download URL in a new
// tab. The buttons are disabled while the mutation is in flight, and
// show a toast (success or error) via react-hot-toast.
//
// The component is intentionally minimal — it reuses the existing
// `.btn .btn-outline` design-system classes (per project conventions)
// and never branches on locale. The mutation returns the actual locale
// (`Restaurant.locale`) so the toast can confirm the file's language
// if the operator cares.

import { useTranslation } from "react-i18next";
import { useExportReservations } from "./useExportReservations";

interface ExportButtonProps {
  fromDate: string;
  toDate: string;
}

export function ExportButton({ fromDate, toDate }: ExportButtonProps) {
  const { t } = useTranslation();
  const mutation = useExportReservations();

  const handleExport = (format: "CSV" | "PDF") => {
    mutation.mutate({ format, from: fromDate, to: toDate });
  };

  return (
    <div className="flex gap-2" data-testid="reservations-export">
      <button
        type="button"
        onClick={() => handleExport("CSV")}
        disabled={mutation.isPending}
        className="btn btn-outline"
        data-testid="reservations-export-csv"
      >
        {t("admin.reservations.export.format.csv")}
      </button>
      <button
        type="button"
        onClick={() => handleExport("PDF")}
        disabled={mutation.isPending}
        className="btn btn-outline"
        data-testid="reservations-export-pdf"
      >
        {t("admin.reservations.export.format.pdf")}
      </button>
      {mutation.isError && (
        <p
          role="alert"
          className="text-sm text-error self-center"
          data-testid="reservations-export-error"
        >
          {t("admin.reservations.export.error")}
        </p>
      )}
    </div>
  );
}
